import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { ApiError } from '@/lib/api-client';
import { toast } from '@/utils/toast';
import {
  IncomingListResponseSchema,
  ApproveIncomingResponseSchema,
  RejectIncomingResponseSchema,
  SimulateIncomingResponseSchema,
  ClientListResponseSchema,
  type IncomingListResponse,
  type IncomingPaymentDto,
  type IncomingFilters,
  type SimulateIncomingRequest,
  type SimulateIncomingResponse,
  type ApproveIncomingResponse,
  type RejectIncomingResponse,
  type ClientOption,
  DEFAULT_INCOMING_FILTERS,
} from './types';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const INCOMING_KEY = 'incoming-payments';
const CLIENTS_KEY = 'incoming-clients';

// ---------------------------------------------------------------------------
// Build query params from filter state
// ---------------------------------------------------------------------------

function buildParams(filters: IncomingFilters): Record<string, string> {
  const params: Record<string, string> = {
    page: String(filters.page),
    limit: String(filters.limit),
  };
  if (filters.status !== 'all') params.status = filters.status;
  if (filters.clientId) params.client_id = String(filters.clientId);
  if (filters.dateFrom) params.date_from = filters.dateFrom;
  if (filters.dateTo) params.date_to = filters.dateTo;
  return params;
}

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

export interface UseIncomingReturn {
  data: IncomingPaymentDto[];
  total: number | null;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  filters: IncomingFilters;
  setStatus: (status: IncomingFilters['status']) => void;
  setClientId: (clientId: number | undefined) => void;
  setDateRange: (from: string | undefined, to: string | undefined) => void;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  refresh: () => void;
  approvePayment: (id: number) => void;
  rejectPayment: (id: number) => void;
  simulateIncoming: (data: SimulateIncomingRequest) => Promise<SimulateIncomingResponse>;
  isApproving: boolean;
  isRejecting: boolean;
  clients: ClientOption[];
  isLoadingClients: boolean;
  clientNameById: Map<number, string>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useIncoming(): UseIncomingReturn {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<IncomingFilters>(DEFAULT_INCOMING_FILTERS);

  const params = buildParams(filters);

  // Fetch incoming payments list
  const query = useQuery<IncomingListResponse>({
    queryKey: [INCOMING_KEY, params],
    queryFn: async () => {
      const raw = await api.get<IncomingListResponse>('/api/convera/incoming/payments', { params });
      return IncomingListResponseSchema.parse(raw);
    },
  });

  // Fetch clients for Combobox. The webapp serialises client_id as a string
  // (bigint precision); Zod coerces it back to a finite number so consumers
  // can rely on ClientOption.id being numeric.
  const clientsQuery = useQuery<ClientOption[]>({
    queryKey: [CLIENTS_KEY],
    queryFn: async () => {
      const raw = await api.get<unknown>('/api/clients', {
        params: { page: '1', page_size: '500', sort_by: 'company_name', sort_order: 'asc' },
      });
      const parsed = ClientListResponseSchema.parse(raw);
      return parsed.items.map((c) => ({
        id: c.client_id,
        label: c.company_name,
      }));
    },
    staleTime: 5 * 60_000, // 5 minutes
  });

  // Approve mutation
  const approveMutation = useMutation<ApproveIncomingResponse, ApiError, number>({
    mutationFn: async (incoming_payment_id) => {
      const raw = await api.post<ApproveIncomingResponse>('/api/convera/incoming/approve', {
        incoming_payment_id,
      });
      return ApproveIncomingResponseSchema.parse(raw);
    },
    onSuccess: () => {
      toast.success('Payment approved and credited to account');
      void queryClient.invalidateQueries({ queryKey: [INCOMING_KEY] });
    },
    onError: (err) => {
      toast.error(typeof err.message === 'string' ? err.message : 'Failed to approve payment');
    },
  });

  // Reject mutation
  const rejectMutation = useMutation<RejectIncomingResponse, ApiError, number>({
    mutationFn: async (incoming_payment_id) => {
      const raw = await api.post<RejectIncomingResponse>('/api/convera/incoming/reject', {
        incoming_payment_id,
      });
      return RejectIncomingResponseSchema.parse(raw);
    },
    onSuccess: () => {
      toast.success('Payment rejected');
      void queryClient.invalidateQueries({ queryKey: [INCOMING_KEY] });
    },
    onError: (err) => {
      toast.error(typeof err.message === 'string' ? err.message : 'Failed to reject payment');
    },
  });

  // Filter setters — reset page to 1 when filters change
  const setStatus = useCallback((status: IncomingFilters['status']) => {
    setFilters((prev) => ({ ...prev, status, page: 1 }));
  }, []);

  const setClientId = useCallback((clientId: number | undefined) => {
    setFilters((prev) => ({ ...prev, clientId, page: 1 }));
  }, []);

  const setDateRange = useCallback((from: string | undefined, to: string | undefined) => {
    setFilters((prev) => ({ ...prev, dateFrom: from, dateTo: to, page: 1 }));
  }, []);

  const setPage = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const setLimit = useCallback((limit: number) => {
    setFilters((prev) => ({ ...prev, limit, page: 1 }));
  }, []);

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [INCOMING_KEY] });
  }, [queryClient]);

  // Lookup map for resolving a client_id to its display name. Built from the
  // already-fetched clients list so the table can render a Receiver column
  // without an additional request.
  const clientNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const c of clientsQuery.data ?? []) {
      map.set(c.id, c.label);
    }
    return map;
  }, [clientsQuery.data]);

  // Simulate incoming wire (dev-only)
  const simulateIncoming = useCallback(
    async (data: SimulateIncomingRequest): Promise<SimulateIncomingResponse> => {
      const payload = {
        client_id: data.client_id,
        amount: data.amount,
        currency_code: data.currency_code,
        sender_name: data.sender_name,
        sender_account: data.sender_account || undefined,
        reference: data.reference || undefined,
      };
      const raw = await api.post<SimulateIncomingResponse>(
        '/api/convera/incoming/simulate',
        payload,
      );
      return SimulateIncomingResponseSchema.parse(raw);
    },
    [],
  );

  return {
    data: query.data?.data ?? [],
    total: query.data?.meta.total ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    filters,
    setStatus,
    setClientId,
    setDateRange,
    setPage,
    setLimit,
    refresh,
    approvePayment: (id) => approveMutation.mutate(id),
    rejectPayment: (id) => rejectMutation.mutate(id),
    simulateIncoming,
    isApproving: approveMutation.isPending,
    isRejecting: rejectMutation.isPending,
    clients: clientsQuery.data ?? [],
    isLoadingClients: clientsQuery.isLoading,
    clientNameById,
  };
}
